import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Pencil, Trash2, ChevronRight, GripVertical,
  Layers, Hash, Target as TargetIcon, BookOpen, Search,
} from 'lucide-react';
import { useT } from '@/store/i18n';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { useAuth } from '@/store/auth';
import { useDragSort } from '@/hooks/useDragSort';
import { cn } from '@/lib/cn';
import { api } from '@/lib/api';
import type { SubjectGroup, GradeLevel } from '@/types';

interface Strand {
  id: number;
  subject_group_id: number;
  strand_number: number;
  name: string;
  description?: string;
  sort_order?: number;
  is_active?: 0 | 1;
}
interface Standard {
  id: number;
  learning_strand_id?: number;
  subject_group_id: number;
  grade_level_id: number;
  code: string;
  name: string;
  description?: string;
  sort_order?: number;
}
interface Indicator {
  id: number;
  learning_standard_id?: number;
  subject_group_id: number;
  grade_level_id: number;
  code: string;
  title: string;
  description?: string;
  sort_order?: number;
}

type EditTarget =
  | { kind: 'strand'; row: Partial<Strand> }
  | { kind: 'standard'; row: Partial<Standard> }
  | { kind: 'indicator'; row: Partial<Indicator> }
  | null;

export function LearningIndicators() {
  const t = useT();
  const qc = useQueryClient();
  const role = useAuth((s) => s.user?.role);
  const canEdit = role === 'admin' || role === 'academic_admin';

  const [subjectId, setSubjectId] = useState<number | null>(null);
  const [strandId, setStrandId] = useState<number | null>(null);
  const [standardId, setStandardId] = useState<number | null>(null);
  const [editing, setEditing] = useState<EditTarget>(null);
  const [search, setSearch] = useState('');

  // ─── Data queries ─────────────────────────────────────
  const meta = useQuery({
    queryKey: ['metadata-full'],
    queryFn: () =>
      api<{ data?: { subject_groups?: SubjectGroup[]; grade_levels?: GradeLevel[] } }>('metadata/all.php')
        .then((r) => r.data ?? {}),
    staleTime: 5 * 60_000,
  });
  const subjects = meta.data?.subject_groups ?? [];
  const grades = meta.data?.grade_levels ?? [];

  const strandsQ = useQuery({
    queryKey: ['strands', subjectId],
    queryFn: async () => {
      if (!subjectId) return [];
      const r = await api<{ data?: { learningStrands?: Strand[] } }>(`learning-strands.php?subject_group_id=${subjectId}`);
      return r.data?.learningStrands ?? [];
    },
    enabled: !!subjectId,
  });

  const standardsQ = useQuery({
    queryKey: ['standards', strandId],
    queryFn: async () => {
      if (!strandId) return [];
      const r = await api<{ data?: { learningStandards?: Standard[] } }>(`learning-standards.php?learning_strand_id=${strandId}`);
      return r.data?.learningStandards ?? [];
    },
    enabled: !!strandId,
  });

  const indicatorsQ = useQuery({
    queryKey: ['indicators', standardId],
    queryFn: async () => {
      if (!standardId) return [];
      const r = await api<{ data?: { learningIndicators?: Indicator[] } }>(`learning-indicators.php?learning_standard_id=${standardId}`);
      return r.data?.learningIndicators ?? [];
    },
    enabled: !!standardId,
  });

  // Save / delete mutations (generic across kinds)
  const save = useMutation({
    mutationFn: async (input: { kind: 'strand' | 'standard' | 'indicator'; data: any; id?: number }) => {
      const endpoint =
        input.kind === 'strand' ? 'learning-strands.php' :
        input.kind === 'standard' ? 'learning-standards.php' :
        'learning-indicators.php';
      if (input.id) {
        return api(`${endpoint}?id=${input.id}`, { method: 'PUT', json: input.data });
      }
      return api(endpoint, { method: 'POST', json: input.data });
    },
    onSuccess: (_, vars) => {
      toast.success(vars.id ? t('learning.save_success') : t('learning.add_success'));
      qc.invalidateQueries({ queryKey: [vars.kind === 'strand' ? 'strands' : vars.kind === 'standard' ? 'standards' : 'indicators'] });
      setEditing(null);
    },
    onError: (e: Error) => toast.error(t('error.save_failed'), e.message),
  });

  const remove = useMutation({
    mutationFn: async (input: { kind: 'strand' | 'standard' | 'indicator'; id: number }) => {
      const endpoint =
        input.kind === 'strand' ? 'learning-strands.php' :
        input.kind === 'standard' ? 'learning-standards.php' :
        'learning-indicators.php';
      return api(`${endpoint}?id=${input.id}`, { method: 'DELETE' });
    },
    onSuccess: (_, vars) => {
      toast.success(t('learning.delete_success'));
      qc.invalidateQueries({ queryKey: [vars.kind === 'strand' ? 'strands' : vars.kind === 'standard' ? 'standards' : 'indicators'] });
      // Cascade clear selection if deleted current
      if (vars.kind === 'strand' && vars.id === strandId) { setStrandId(null); setStandardId(null); }
      if (vars.kind === 'standard' && vars.id === standardId) setStandardId(null);
    },
    onError: (e: Error) => toast.error(t('error.delete_failed'), e.message),
  });

  // Drag-drop reorder: send PUT for each affected item with new sort_order
  async function reorder(kind: 'strand' | 'standard' | 'indicator', items: any[]) {
    const endpoint =
      kind === 'strand' ? 'learning-strands.php' :
      kind === 'standard' ? 'learning-standards.php' :
      'learning-indicators.php';
    const queryKey = kind === 'strand' ? 'strands' : kind === 'standard' ? 'standards' : 'indicators';
    // Optimistic local update
    const cacheKey = kind === 'strand' ? ['strands', subjectId] : kind === 'standard' ? ['standards', strandId] : ['indicators', standardId];
    qc.setQueryData(cacheKey, items);
    // Persist
    try {
      await Promise.all(items.map((it, i) =>
        api(`${endpoint}?id=${it.id}`, { method: 'PUT', json: { ...it, sort_order: i } })
      ));
      toast.success(t('learning.reorder_success'));
    } catch (e) {
      toast.error(t('learning.reorder_fail'), (e as Error).message);
      qc.invalidateQueries({ queryKey: [queryKey] });
    }
  }

  function reorderArray<T>(arr: T[], from: number, to: number): T[] {
    const next = arr.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  }

  const strandDrag = useDragSort(strandsQ.data?.length ?? 0, async (from, to) => {
    if (!strandsQ.data) return;
    await reorder('strand', reorderArray(strandsQ.data, from, to));
  });
  const standardDrag = useDragSort(standardsQ.data?.length ?? 0, async (from, to) => {
    if (!standardsQ.data) return;
    await reorder('standard', reorderArray(standardsQ.data, from, to));
  });
  const indicatorDrag = useDragSort(indicatorsQ.data?.length ?? 0, async (from, to) => {
    if (!indicatorsQ.data) return;
    await reorder('indicator', reorderArray(indicatorsQ.data, from, to));
  });

  // Filter helpers
  const filteredSubjects = useMemo(() => {
    if (!search) return subjects;
    const s = search.toLowerCase();
    return subjects.filter((x) => x.name.toLowerCase().includes(s) || x.code.toLowerCase().includes(s));
  }, [subjects, search]);

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('learning.title')}
        description={t('learning.desc')}
        actions={
          <Input
            leftIcon={<Search className="w-4 h-4" />}
            placeholder={t('learning.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72"
          />
        }
      />

      {/* Breadcrumb / chain */}
      <Card className="p-3 flex flex-wrap items-center gap-2 text-sm">
        <ChainItem
          icon={<Layers className="w-4 h-4" />}
          label={subjectId ? subjects.find((s) => s.id === subjectId)?.name ?? t('learning.col.subject') : t('learning.choose_subject')}
          active={!!subjectId}
          onClear={() => { setSubjectId(null); setStrandId(null); setStandardId(null); }}
        />
        <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <ChainItem
          icon={<Hash className="w-4 h-4" />}
          label={strandId ? `${t('learning.col.strand')}${strandsQ.data?.find((s) => s.id === strandId)?.strand_number ?? ''} ${strandsQ.data?.find((s) => s.id === strandId)?.name ?? ''}` : t('learning.choose_strand')}
          active={!!strandId}
          onClear={() => { setStrandId(null); setStandardId(null); }}
          disabled={!subjectId}
        />
        <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <ChainItem
          icon={<BookOpen className="w-4 h-4" />}
          label={standardId ? standardsQ.data?.find((s) => s.id === standardId)?.code ?? t('learning.col.standard') : t('learning.choose_standard')}
          active={!!standardId}
          onClear={() => setStandardId(null)}
          disabled={!strandId}
        />
        <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <span className="text-slate-400 text-xs px-2">{t('learning.col.indicator')}</span>
      </Card>

      {/* 4-column layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Col 1: Subject groups */}
        <Column
          title={t('learning.col.subject')}
          subtitle={`${filteredSubjects.length} ${t('learning.col.subject')}`}
          tone="brand"
          loading={meta.isLoading}
          empty={filteredSubjects.length === 0}
        >
          {filteredSubjects.map((s) => (
            <ColumnRow
              key={s.id}
              active={s.id === subjectId}
              onClick={() => { setSubjectId(s.id); setStrandId(null); setStandardId(null); }}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.name}</div>
                <div className="text-xs text-slate-400 font-mono">{s.code}</div>
              </div>
            </ColumnRow>
          ))}
        </Column>

        {/* Col 2: Strands */}
        <Column
          title={t('learning.col.strand')}
          subtitle={subjectId ? `${strandsQ.data?.length ?? 0} ${t('learning.strand_unit')}` : t('learning.select_subject')}
          tone="info"
          loading={!!subjectId && strandsQ.isLoading}
          empty={!subjectId || (strandsQ.data?.length ?? 0) === 0}
          emptyText={!subjectId ? `— ${t('learning.choose_subject')} —` : t('learning.no_strand')}
          onAdd={canEdit && subjectId ? () => setEditing({ kind: 'strand', row: { subject_group_id: subjectId } }) : undefined}
        >
          {(strandsQ.data ?? []).map((s, i) => (
            <ColumnRow
              key={s.id}
              active={s.id === strandId}
              onClick={() => { setStrandId(s.id); setStandardId(null); }}
              dragHandlers={canEdit ? strandDrag.handlers(i) : undefined}
              isDragOver={strandDrag.dragOver === i && strandDrag.dragging !== i}
              isDragging={strandDrag.dragging === i}
              actions={canEdit ? (
                <RowActions
                  onEdit={() => setEditing({ kind: 'strand', row: s })}
                  onDelete={async () => {
                    const ok = await confirmDialog({ title: `${t('btn.delete')} "${s.name}"?`, description: t('learning.delete_strand'), destructive: true, confirmLabel: t('btn.delete') });
                    if (ok) remove.mutate({ kind: 'strand', id: s.id });
                  }}
                />
              ) : null}
            >
              <span className="inline-flex w-7 h-7 rounded-md bg-blue-100 text-blue-700 text-xs font-semibold items-center justify-center flex-shrink-0">
                {s.strand_number}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.name}</div>
                {s.description && <div className="text-xs text-slate-400 truncate">{s.description}</div>}
              </div>
            </ColumnRow>
          ))}
        </Column>

        {/* Col 3: Standards */}
        <Column
          title={t('learning.col.standard')}
          subtitle={strandId ? `${standardsQ.data?.length ?? 0} ${t('learning.standard_unit')}` : t('learning.select_strand')}
          tone="violet"
          loading={!!strandId && standardsQ.isLoading}
          empty={!strandId || (standardsQ.data?.length ?? 0) === 0}
          emptyText={!strandId ? `— ${t('learning.choose_strand')} —` : t('learning.no_standard')}
          onAdd={canEdit && strandId ? () => setEditing({ kind: 'standard', row: { learning_strand_id: strandId, subject_group_id: subjectId ?? 0 } }) : undefined}
        >
          {(standardsQ.data ?? []).map((s, i) => (
            <ColumnRow
              key={s.id}
              active={s.id === standardId}
              onClick={() => setStandardId(s.id)}
              dragHandlers={canEdit ? standardDrag.handlers(i) : undefined}
              isDragOver={standardDrag.dragOver === i && standardDrag.dragging !== i}
              isDragging={standardDrag.dragging === i}
              actions={canEdit ? (
                <RowActions
                  onEdit={() => setEditing({ kind: 'standard', row: s })}
                  onDelete={async () => {
                    const ok = await confirmDialog({ title: `${t('btn.delete')} "${s.code}"?`, description: t('learning.delete_standard'), destructive: true, confirmLabel: t('btn.delete') });
                    if (ok) remove.mutate({ kind: 'standard', id: s.id });
                  }}
                />
              ) : null}
            >
              <Badge tone="info" className="font-mono">{s.code}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.name || s.description}</div>
                {s.description && s.name && <div className="text-xs text-slate-400 truncate">{s.description}</div>}
              </div>
            </ColumnRow>
          ))}
        </Column>

        {/* Col 4: Indicators */}
        <Column
          title={t('learning.col.indicator')}
          subtitle={standardId ? `${indicatorsQ.data?.length ?? 0} ${t('learning.indicator_unit')}` : t('learning.select_standard')}
          tone="warning"
          loading={!!standardId && indicatorsQ.isLoading}
          empty={!standardId || (indicatorsQ.data?.length ?? 0) === 0}
          emptyText={!standardId ? `— ${t('learning.choose_standard')} —` : t('learning.no_indicator')}
          onAdd={canEdit && standardId ? () => setEditing({ kind: 'indicator', row: { learning_standard_id: standardId, subject_group_id: subjectId ?? 0, grade_level_id: 0 } }) : undefined}
        >
          {(indicatorsQ.data ?? []).map((i, idx) => (
            <ColumnRow
              key={i.id}
              dragHandlers={canEdit ? indicatorDrag.handlers(idx) : undefined}
              isDragOver={indicatorDrag.dragOver === idx && indicatorDrag.dragging !== idx}
              isDragging={indicatorDrag.dragging === idx}
              actions={canEdit ? (
                <RowActions
                  onEdit={() => setEditing({ kind: 'indicator', row: i })}
                  onDelete={async () => {
                    const ok = await confirmDialog({ title: `${t('btn.delete')} "${i.code}"?`, destructive: true, confirmLabel: t('btn.delete') });
                    if (ok) remove.mutate({ kind: 'indicator', id: i.id });
                  }}
                />
              ) : null}
            >
              <Badge tone="warning" className="font-mono">{i.code}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{i.title}</div>
                {i.description && <div className="text-xs text-slate-400 line-clamp-2">{i.description}</div>}
              </div>
            </ColumnRow>
          ))}
        </Column>
      </div>

      {/* Edit modals */}
      {editing?.kind === 'strand' && (
        <StrandModal
          row={editing.row}
          onClose={() => setEditing(null)}
          onSave={(d) => save.mutate({ kind: 'strand', data: d, id: editing.row.id })}
          saving={save.isPending}
        />
      )}
      {editing?.kind === 'standard' && (
        <StandardModal
          row={editing.row}
          grades={grades}
          onClose={() => setEditing(null)}
          onSave={(d) => save.mutate({ kind: 'standard', data: d, id: editing.row.id })}
          saving={save.isPending}
        />
      )}
      {editing?.kind === 'indicator' && (
        <IndicatorModal
          row={editing.row}
          grades={grades}
          onClose={() => setEditing(null)}
          onSave={(d) => save.mutate({ kind: 'indicator', data: d, id: editing.row.id })}
          saving={save.isPending}
        />
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────

const COLUMN_TONES = {
  brand: 'bg-brand-50 text-brand-700 border-brand-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
};

function Column({
  title, subtitle, tone, children, loading, empty, emptyText, onAdd,
}: {
  title: string;
  subtitle?: string;
  tone: keyof typeof COLUMN_TONES;
  children: React.ReactNode;
  loading?: boolean;
  empty?: boolean;
  emptyText?: string;
  onAdd?: () => void;
}) {
  const t = useT();
  return (
    <Card className="flex flex-col h-[70vh] min-h-[500px] overflow-hidden">
      <div className={cn('px-4 py-3 border-b flex items-center justify-between gap-2', COLUMN_TONES[tone])}>
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          {subtitle && <p className="text-[11px] opacity-70 mt-0.5">{subtitle}</p>}
        </div>
        {onAdd && (
          <Button variant="ghost" size="sm" leftIcon={<Plus className="w-3.5 h-3.5" />} onClick={onAdd}>
            {t('btn.add')}
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-3 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : empty ? (
          <div className="p-6 text-center text-sm text-slate-400">{emptyText ?? t('learning.empty')}</div>
        ) : (
          <ul className="divide-y divide-slate-100">{children}</ul>
        )}
      </div>
    </Card>
  );
}

function ColumnRow({
  children, active, onClick, actions, dragHandlers, isDragOver, isDragging,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  actions?: React.ReactNode;
  dragHandlers?: {
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (e: React.DragEvent) => void;
    onDragEnd: () => void;
  };
  isDragOver?: boolean;
  isDragging?: boolean;
}) {
  return (
    <li
      draggable={!!dragHandlers}
      {...(dragHandlers ?? {})}
      className={cn(
        'group flex items-start gap-2 px-3 py-2.5 transition-colors',
        onClick && 'cursor-pointer hover:bg-slate-50',
        active && 'bg-brand-50/60 border-l-2 border-brand-600',
        isDragOver && 'border-t-2 border-brand-500 bg-brand-50/30',
        isDragging && 'opacity-40',
      )}
      onClick={onClick}
    >
      <span className={cn('mt-1 cursor-grab text-slate-300 group-hover:text-slate-400', dragHandlers && 'active:cursor-grabbing')} aria-hidden="true">
        <GripVertical className="w-3.5 h-3.5" />
      </span>
      {children}
      {actions && <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">{actions}</div>}
    </li>
  );
}

function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const t = useT();
  return (
    <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={onEdit} aria-label={t('btn.edit')} className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100">
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={onDelete} aria-label={t('btn.delete')} className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function ChainItem({
  icon, label, active, onClear, disabled,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClear?: () => void;
  disabled?: boolean;
}) {
  const t = useT();
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs',
        disabled
          ? 'text-slate-300'
          : active
            ? 'bg-brand-100 text-brand-800 font-medium'
            : 'text-slate-500'
      )}
    >
      {icon}
      <span className="truncate max-w-[180px]">{label}</span>
      {active && onClear && (
        <button type="button" onClick={onClear} aria-label={t('btn.clear')} className="text-current opacity-50 hover:opacity-100">
          ×
        </button>
      )}
    </span>
  );
}

// ─── Edit modals ────────────────────────────────────

function StrandModal({ row, onClose, onSave, saving }: {
  row: Partial<Strand>; onClose: () => void; onSave: (d: any) => void; saving: boolean;
}) {
  const t = useT();
  const [name, setName] = useState(row.name ?? '');
  const [num, setNum] = useState(row.strand_number ?? 1);
  const [desc, setDesc] = useState(row.description ?? '');

  return (
    <Modal
      open
      onClose={onClose}
      title={row.id ? t('learning.strand_modal_edit') : t('learning.strand_modal_add')}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('btn.cancel')}</Button>
          <Button
            loading={saving}
            onClick={() => onSave({
              subject_group_id: row.subject_group_id,
              strand_number: num,
              name: name.trim(),
              description: desc.trim() || null,
            })}
            disabled={!name.trim()}
          >
            {t('btn.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label={t('learning.field.strand_number')} type="number" min={1} value={num} onChange={(e) => setNum(parseInt(e.target.value, 10) || 1)} />
        <Input label={t('learning.field.strand_name')} value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        <Textarea label={t('metadata.field.description')} value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} />
      </div>
    </Modal>
  );
}

function StandardModal({ row, grades, onClose, onSave, saving }: {
  row: Partial<Standard>; grades: GradeLevel[]; onClose: () => void; onSave: (d: any) => void; saving: boolean;
}) {
  const t = useT();
  const [code, setCode] = useState(row.code ?? '');
  const [name, setName] = useState(row.name ?? '');
  const [desc, setDesc] = useState(row.description ?? '');
  const [grade, setGrade] = useState(row.grade_level_id ?? 0);

  return (
    <Modal
      open
      onClose={onClose}
      title={row.id ? t('learning.standard_modal_edit') : t('learning.standard_modal_add')}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('btn.cancel')}</Button>
          <Button
            loading={saving}
            onClick={() => onSave({
              learning_strand_id: row.learning_strand_id,
              subject_group_id: row.subject_group_id,
              grade_level_id: grade || null,
              code: code.trim(),
              name: name.trim(),
              description: desc.trim() || null,
            })}
            disabled={!code.trim() || !name.trim()}
          >
            {t('btn.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label={t('learning.field.standard_code')} value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
        <Input label={t('learning.field.standard_name')} value={name} onChange={(e) => setName(e.target.value)} required />
        <Select label={t('learning.field.grade')} value={String(grade)} onChange={(e) => setGrade(parseInt(e.target.value, 10))}>
          <option value="0">{t('learning.grade_all')}</option>
          {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </Select>
        <Textarea label={t('metadata.field.description')} value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} />
      </div>
    </Modal>
  );
}

function IndicatorModal({ row, grades, onClose, onSave, saving }: {
  row: Partial<Indicator>; grades: GradeLevel[]; onClose: () => void; onSave: (d: any) => void; saving: boolean;
}) {
  const t = useT();
  const [code, setCode] = useState(row.code ?? '');
  const [title, setTitle] = useState(row.title ?? '');
  const [desc, setDesc] = useState(row.description ?? '');
  const [grade, setGrade] = useState(row.grade_level_id ?? 0);

  return (
    <Modal
      open
      onClose={onClose}
      title={row.id ? t('learning.indicator_modal_edit') : t('learning.indicator_modal_add')}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('btn.cancel')}</Button>
          <Button
            loading={saving}
            onClick={() => onSave({
              learning_standard_id: row.learning_standard_id,
              subject_group_id: row.subject_group_id,
              grade_level_id: grade || null,
              code: code.trim(),
              title: title.trim(),
              description: desc.trim() || null,
            })}
            disabled={!code.trim() || !title.trim() || !grade}
          >
            {t('btn.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label={t('learning.field.indicator_code')} value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
        <Input label={t('learning.field.indicator_title')} value={title} onChange={(e) => setTitle(e.target.value)} required />
        <Select label={t('learning.field.grade_required')} value={String(grade)} onChange={(e) => setGrade(parseInt(e.target.value, 10))}>
          <option value="0">{t('learning.grade_choose')}</option>
          {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </Select>
        <Textarea label={t('metadata.field.description')} value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} />
      </div>
    </Modal>
  );
}
